import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Blue60 } from "styles/buildertrendTheme/Colors";

import { IconComments, IconCommentsFilled } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BdsRollupButton } from "@buildertrend/components";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconComments, BTIconCommentsFilled } from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";

import "./CommentComponentButtons.less";

export const COMMENTS_UNAVAILABLE_BEFORE_SAVE_TOOLTIP = "Comments will be available after you save";

interface ICommentsButtonForPageProps {
    id: string;
    entityId: number;
    onClick: () => void;
    lastUnreadCommentId: number | null;
    hasUnreadComments: boolean;
    hasComments?: boolean;
    isCommentDrawerOpen: boolean;
    disabled?: boolean;
    tooltip?: React.ReactNode;

    /**
     * BdsButtonGroup scans for direct children with these props.
     * Needed when CommentButtonForPage is used inside a BdsButtonGroup.
     */
    icon: ReactNode;
    text: string;
    className?: string;

    /**
     * When true, renders the plain `BTButton`-based comment button (no `BdsRollupButton` /
     * `BdsButtonGroup` dependency). Use for headers that surface the comment button as a
     * standalone icon action instead of inside a `BdsButtonGroup`.
     */
    useNewBTButton?: boolean;
}

interface ICommentsBTButtonProps {
    entityId: number;
    handleClick: () => void;
    hasComments: boolean;
    hasUnreadComments: boolean;
    hasCommentBeenRead: boolean;
    isCommentDrawerOpen: boolean;
    style?: React.CSSProperties;
    className?: string;
    disabled?: boolean;
    tooltip?: React.ReactNode;
    type?: "tertiary" | "secondary";
    /**
     * When true, the button is enabled even when the entity is unsaved (entityId === 0).
     * The host is expected to auto-save the entity before persisting any comment.
     */
    allowBeforeSave?: boolean;
    /**
     * When true, the button will use the new BTButton component instead of the legacy BTButtonDeprecated component.
     * The new BTButton component has a different style and behavior, and is preferred for new development.
     * The legacy BTButtonDeprecated component is kept for backward compatibility.
     */
    useNewBTButton?: boolean;
}

/**
 * Don't use directly, use CommentDrawer or CommentButtonForPage
 */
export const CommentsBTButton = ({
    entityId,
    handleClick,
    hasUnreadComments,
    hasCommentBeenRead,
    isCommentDrawerOpen,
    style,
    className,
    hasComments,
    disabled,
    tooltip,
    type,
    allowBeforeSave,
    useNewBTButton,
}: ICommentsBTButtonProps) => {
    const blockBecauseUnsaved = entityId === 0 && !allowBeforeSave;
    return (
        <BTPopover
            disabled={!disabled && !blockBecauseUnsaved}
            content={tooltip ?? COMMENTS_UNAVAILABLE_BEFORE_SAVE_TOOLTIP}
        >
            {useNewBTButton && (
                <BTButton
                    icon={hasComments ? <IconCommentsFilled /> : <IconComments />}
                    type={type ?? "tertiary"}
                    selected={isCommentDrawerOpen}
                    data-testid="openCloseCommentDrawer"
                    onClick={handleClick}
                    className={cn("CommentsBTButton", className, {
                        UnreadCommentsIndicator: hasUnreadComments && !hasCommentBeenRead,
                        CommentDrawerOpen: isCommentDrawerOpen,
                    })}
                    style={style}
                    aria-label="Comments"
                    disabled={disabled || blockBecauseUnsaved}
                />
            )}
            {!useNewBTButton && (
                <BTButtonDeprecated
                    icon={
                        hasComments ? (
                            <BTIconCommentsFilled
                                title="Comments"
                                size={20}
                                style={{ color: Blue60 }}
                            />
                        ) : (
                            <BTIconComments title="Comments" size={20} />
                        )
                    }
                    type={type ?? "tertiary"}
                    isolated
                    data-testid="openCloseCommentDrawer"
                    onClick={handleClick}
                    className={cn("CommentsBTButton", className, {
                        UnreadCommentsIndicator: hasUnreadComments && !hasCommentBeenRead,
                        CommentDrawerOpen: isCommentDrawerOpen,
                    })}
                    style={style}
                    aria-label="Comments"
                    disabled={disabled || blockBecauseUnsaved}
                />
            )}
        </BTPopover>
    );
};

interface ICommentsBdsButtonProps {
    id: string;
    entityId: number;
    onClick: () => void;
    hasUnreadComments: boolean;
    hasCommentBeenRead: boolean;
    isCommentDrawerOpen: boolean;
    disabled?: boolean;
    tooltip?: React.ReactNode;
    className?: string;
    icon: ReactNode;
    hasComments?: boolean;
    /**
     * When true, the button is enabled even when the entity is unsaved (entityId === 0).
     * The host is expected to auto-save the entity before persisting any comment.
     */
    allowBeforeSave?: boolean;
}

/**
 * Don't use directly, use CommentDrawer or CommentButtonForPage
 */
export const CommentsBdsButton = ({
    id,
    entityId,
    onClick,
    hasUnreadComments,
    hasCommentBeenRead,
    isCommentDrawerOpen,
    disabled,
    tooltip,
    className,
    icon,
    hasComments,
    allowBeforeSave,
}: ICommentsBdsButtonProps) => {
    const blockBecauseUnsaved = entityId === 0 && !allowBeforeSave;
    const defaultTooltip = blockBecauseUnsaved
        ? COMMENTS_UNAVAILABLE_BEFORE_SAVE_TOOLTIP
        : "Comments";
    return (
        <BdsRollupButton
            id={id}
            testid="openCloseCommentDrawerBds"
            onClick={onClick}
            value="comments"
            ariaLabel={hasComments ? "CommentsFilled" : "Comments"}
            icon={icon}
            displayType="tertiary"
            tooltip={tooltip ?? defaultTooltip}
            selected={isCommentDrawerOpen}
            className={cn("CommentsBdsButton", className, {
                UnreadCommentsIndicator: hasUnreadComments && !hasCommentBeenRead,
                CommentDrawerOpen: isCommentDrawerOpen,
                "bds-button--selected": isCommentDrawerOpen,
                HasComments: hasComments,
            })}
            disabled={disabled || blockBecauseUnsaved}
            maxStage={1}
        />
    );
};

/**
 * Use when you cannot use the CommentDrawer component
 */
export const CommentsButtonForPage = ({
    id,
    entityId,
    onClick,
    lastUnreadCommentId,
    hasUnreadComments,
    isCommentDrawerOpen,
    tooltip,
    disabled,
    className,
    icon,
    hasComments,
    useNewBTButton,
}: ICommentsButtonForPageProps) => {
    const [hasCommentBeenRead, setHasCommentBeenRead] = useState(false);

    const handleClick = useCallback(() => {
        onClick();
        setHasCommentBeenRead(true);
    }, [onClick]);

    useEffect(() => {
        setHasCommentBeenRead(false);
    }, [lastUnreadCommentId]);

    if (useNewBTButton) {
        return (
            <CommentsBTButton
                entityId={entityId}
                handleClick={handleClick}
                hasUnreadComments={hasUnreadComments}
                hasCommentBeenRead={hasCommentBeenRead}
                isCommentDrawerOpen={isCommentDrawerOpen}
                tooltip={tooltip}
                disabled={disabled}
                className={className}
                hasComments={hasComments ?? false}
                useNewBTButton
            />
        );
    }

    return (
        <CommentsBdsButton
            id={id}
            entityId={entityId}
            hasUnreadComments={hasUnreadComments}
            hasCommentBeenRead={hasCommentBeenRead}
            isCommentDrawerOpen={isCommentDrawerOpen}
            onClick={handleClick}
            tooltip={tooltip}
            disabled={disabled}
            className={className}
            icon={icon}
            hasComments={hasComments}
        />
    );
};
